/**
 * WebSocket Vuex 模块
 * 连接成功后发送：{ type: 'login', user_id }
 */
import WebSocketManager from '@/utils/websocket'
import { playTipSound } from '@/utils/tipSound'
import { WS_URL } from '@/config'

const state = {
  websocket: null,
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  messageQueue: [],
  receivedMessages: [],
  loggedInUserId: null,
  config: {
    url: WS_URL,
    heartbeatInterval: 30000,
    reconnectInterval: 3000,
    autoReconnect: true
  },
  lastError: null,
  currentChatId: null,
  chatMessages: {},
  unreadCount: 0
}

const mutations = {
  SET_WEBSOCKET(state, websocket) {
    state.websocket = websocket
  },
  SET_CONNECTION_STATUS(state, { isConnected, isConnecting, isReconnecting }) {
    if (isConnected !== undefined) state.isConnected = isConnected
    if (isConnecting !== undefined) state.isConnecting = isConnecting
    if (isReconnecting !== undefined) state.isReconnecting = isReconnecting
  },
  SET_RECONNECT_INFO(state, { attempts, maxAttempts }) {
    if (attempts !== undefined) state.reconnectAttempts = attempts
    if (maxAttempts !== undefined) state.maxReconnectAttempts = maxAttempts
  },
  ADD_MESSAGE_TO_QUEUE(state, message) {
    state.messageQueue.push({
      ...message,
      timestamp: Date.now(),
      id: Date.now() + Math.random()
    })
  },
  CLEAR_MESSAGE_QUEUE(state) {
    state.messageQueue = []
  },
  ADD_RECEIVED_MESSAGE(state, message) {
    state.receivedMessages.unshift({
      ...message,
      timestamp: Date.now(),
      _trackId: Date.now() + Math.random()
    })
    if (state.receivedMessages.length > 100) {
      state.receivedMessages = state.receivedMessages.slice(0, 100)
    }
  },
  UPDATE_CONFIG(state, config) {
    state.config = { ...state.config, ...config }
  },
  SET_ERROR(state, error) {
    state.lastError = {
      message: (error && error.message) || error,
      timestamp: Date.now()
    }
  },
  CLEAR_ERROR(state) {
    state.lastError = null
  },
  SET_LOGGED_IN_USER(state, userId) {
    state.loggedInUserId = userId ? Number(userId) : null
  },
  SET_CURRENT_CHAT_ID(state, chatId) {
    state.currentChatId = chatId
  },
  ADD_CHAT_MESSAGE(state, { chatId, message }) {
    if (!state.chatMessages[chatId]) {
      state.chatMessages[chatId] = []
    }
    state.chatMessages[chatId].push({
      ...message,
      timestamp: Date.now(),
      _trackId: Date.now() + Math.random()
    })
    if (state.chatMessages[chatId].length > 500) {
      state.chatMessages[chatId] = state.chatMessages[chatId].slice(-500)
    }
  },
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count
  },
  INCREMENT_UNREAD_COUNT(state) {
    state.unreadCount += 1
  },
  CLEAR_CHAT_MESSAGES(state, chatId) {
    if (chatId) {
      delete state.chatMessages[chatId]
    } else {
      state.chatMessages = {}
    }
  }
}

function getCurrentUserId(rootState) {
  return rootState.user && rootState.user.id ? Number(rootState.user.id) : null
}

function getToken(rootState) {
  return rootState.token || ''
}

const actions = {
  async initWebSocket({ commit, state, rootState }, config = {}) {
    const token = config.token || getToken(rootState)
    if (!token) {
      throw new Error('缺少 token，无法连接 WebSocket')
    }

    const finalConfig = {
      ...state.config,
      ...config,
      url: config.url || state.config.url || WS_URL,
      token
    }
    commit('UPDATE_CONFIG', finalConfig)

    const existing = state.websocket
    if (existing) {
      const status = existing.getStatus ? existing.getStatus() : {}
      if (status.isConnected || status.isConnecting) {
        return existing
      }
      try {
        existing.close()
      } catch (e) {
        // ignore
      }
      commit('SET_WEBSOCKET', null)
    }

    const websocket = new WebSocketManager(finalConfig)
    commit('SET_WEBSOCKET', websocket)

    websocket.on('open', () => {
      commit('SET_CONNECTION_STATUS', {
        isConnected: true,
        isConnecting: false,
        isReconnecting: false
      })
      commit('SET_RECONNECT_INFO', { attempts: 0 })
      commit('CLEAR_ERROR')
    })

    websocket.on('message', (data) => {
      commit('ADD_RECEIVED_MESSAGE', data)
      if (data && data.type === 'login_success') {
        commit('SET_LOGGED_IN_USER', data.user_id)
      }
      const chatId =
        (data && (data.session_id || data.tax_chat_pool_id || data.order_id || data.chat_manage_id)) ||
        null
      if (chatId) {
        commit('ADD_CHAT_MESSAGE', { chatId, message: data })
        if (String(chatId) !== String(state.currentChatId)) {
          commit('INCREMENT_UNREAD_COUNT')
        }
      }

      // 对方新消息：提示音只播一次（跳过系统帧）
      const msgType = data && data.type
      if (msgType === 'login_success' || msgType === 'ping' || msgType === 'pong' || msgType === 'error') {
        return
      }
      const looksLikeChat =
        msgType === 'chat' ||
        !!(data && (
          data.message_type ||
          data.message != null ||
          data.content != null ||
          data.sender_id != null ||
          data.from_user_id != null ||
          chatId
        ))
      if (looksLikeChat) {
        const currentUserId = getCurrentUserId(rootState)
        const senderId =
          (data && (data.sender_id || data.from_user_id || (data.sender && data.sender.id))) ||
          null
        const fromSelf =
          currentUserId != null &&
          senderId != null &&
          Number(senderId) === Number(currentUserId)
        if (!fromSelf) {
          playTipSound()
        }
      }
    })

    websocket.on('error', (error) => {
      commit('SET_ERROR', error)
    })

    websocket.on('close', () => {
      commit('SET_CONNECTION_STATUS', {
        isConnected: false,
        isConnecting: false
      })
      commit('SET_LOGGED_IN_USER', null)
    })

    websocket.on('reconnect', (data) => {
      commit('SET_RECONNECT_INFO', { attempts: data && data.attempts })
      commit('SET_CONNECTION_STATUS', {
        isConnected: !!(websocket.getStatus && websocket.getStatus().isConnected),
        isReconnecting: false
      })
    })

    commit('SET_CONNECTION_STATUS', { isConnecting: true })
    await websocket.connect(token)
    return websocket
  },

  /** 发送任意帧 */
  async sendMessage({ state, commit }, message) {
    try {
      if (state.websocket) {
        const ok = await state.websocket.send(message)
        return ok
      }
      commit('ADD_MESSAGE_TO_QUEUE', message)
      return false
    } catch (error) {
      commit('SET_ERROR', error)
      commit('ADD_MESSAGE_TO_QUEUE', message)
      throw error
    }
  },

  /**
   * 全局登录帧：
   * { type: 'login', user_id }
   */
  async sendLogin({ dispatch, state, rootState, commit }, options = {}) {
    const userId = Number(options.user_id || getCurrentUserId(rootState))
    if (!userId) {
      throw new Error('缺少 user_id，无法发送 WebSocket 登录')
    }
    if (!options.force && Number(state.loggedInUserId) === userId && state.isConnected) {
      return true
    }
    const payload = {
      type: 'login',
      user_id: userId
    }
    const ok = await dispatch('sendMessage', payload)
    if (ok) commit('SET_LOGGED_IN_USER', userId)
    return ok
  },

  /**
   * 发送私聊（自动创建会话，同池），不带 session_id：
   * { type:'chat', to_user_id, tax_chat_pool_id, message, message_type }
   */
  async sendPrivateChat({ dispatch, state }, {
    to_user_id,
    tax_chat_pool_id,
    message,
    message_type = 'text'
  } = {}) {
    if (!to_user_id) throw new Error('缺少 to_user_id')
    if (!tax_chat_pool_id) throw new Error('缺少 tax_chat_pool_id')
    if (message === undefined || message === null || message === '') {
      throw new Error('消息内容不能为空')
    }

    if (!state.isConnected) {
      await dispatch('connectAppWebSocket')
      // 若仍在连接中，短暂等待 open
      if (!state.isConnected) {
        await new Promise((resolve, reject) => {
          const ws = state.websocket
          if (!ws) {
            reject(new Error('WebSocket 未连接'))
            return
          }
          if (ws.getStatus && ws.getStatus().isConnected) {
            resolve()
            return
          }
          const timer = setTimeout(() => {
            off()
            reject(new Error('WebSocket 连接超时'))
          }, 8000)
          const off = ws.on('open', () => {
            clearTimeout(timer)
            off()
            resolve()
          })
        })
      }
    }

    return dispatch('sendMessage', {
      type: 'chat',
      to_user_id: Number(to_user_id),
      tax_chat_pool_id: Number(tax_chat_pool_id),
      message,
      message_type
    })
  },

  /** 发送订单私聊 */
  sendOrderChat({ dispatch }, { to_user_id, tax_chat_pool_id, message }) {
    return dispatch('sendPrivateChat', {
      to_user_id,
      tax_chat_pool_id,
      message: Number(message),
      message_type: 'order'
    })
  },

  /** 发送地址私聊 */
  sendAddressChat({ dispatch }, { to_user_id, tax_chat_pool_id, message }) {
    return dispatch('sendPrivateChat', {
      to_user_id,
      tax_chat_pool_id,
      message: Number(message),
      message_type: 'address'
    })
  },

  /**
   * 登录后建立全局 WebSocket（单例），连接成功自动发送 login
   */
  async connectAppWebSocket({ dispatch, state, rootState }, options = {}) {
    const token = options.token || getToken(rootState)
    const userId = Number(options.user_id || getCurrentUserId(rootState))
    if (!token || !userId) return false

    const sendLogin = () => {
      dispatch('sendLogin', { user_id: userId, force: true }).catch((err) => {
        console.warn('WebSocket 登录失败:', err && err.message)
      })
    }

    const ws = state.websocket
    const live = !!(ws && ws.getStatus && (ws.getStatus().isConnected || ws.getStatus().isConnecting))
    if (ws && live) {
      if (ws.getStatus().isConnected) sendLogin()
      return true
    }
    if (state.isConnecting) return true

    await dispatch('initWebSocket', {
      url: WS_URL,
      token,
      heartbeatInterval: 30000,
      reconnectInterval: 3000,
      autoReconnect: true,
      ...options
    })

    const created = state.websocket
    if (!created) return false

    created.on('open', sendLogin)
    created.on('reconnect', sendLogin)

    if (created.getStatus().isConnected) {
      sendLogin()
    }
    return true
  },

  reconnect({ state, commit }) {
    if (state.websocket) {
      commit('SET_CONNECTION_STATUS', { isReconnecting: true })
      state.websocket.reconnect()
    }
  },

  closeWebSocket({ state, commit }) {
    if (state.websocket) {
      state.websocket.close()
      commit('SET_WEBSOCKET', null)
    }
    commit('SET_CONNECTION_STATUS', {
      isConnected: false,
      isConnecting: false,
      isReconnecting: false
    })
    commit('SET_RECONNECT_INFO', { attempts: 0 })
    commit('SET_LOGGED_IN_USER', null)
    commit('CLEAR_MESSAGE_QUEUE')
  },

  updateConfig({ state, commit }, config) {
    commit('UPDATE_CONFIG', config)
    if (state.websocket) {
      state.websocket.updateConfig(config)
    }
  },

  setCurrentChat({ commit }, chatId) {
    commit('SET_CURRENT_CHAT_ID', chatId)
    commit('SET_UNREAD_COUNT', 0)
  },

  clearChatMessages({ commit }, chatId) {
    commit('CLEAR_CHAT_MESSAGES', chatId)
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  }
}

const getters = {
  connectionStatus: (state) => ({
    isConnected: state.isConnected,
    isConnecting: state.isConnecting,
    isReconnecting: state.isReconnecting,
    reconnectAttempts: state.reconnectAttempts,
    maxReconnectAttempts: state.maxReconnectAttempts
  }),
  latestMessages: (state) => (limit = 10) => state.receivedMessages.slice(0, limit),
  getChatMessages: (state) => (chatId) => state.chatMessages[chatId] || [],
  lastError: (state) => state.lastError,
  messageQueueStatus: (state) => ({
    length: state.messageQueue.length,
    messages: state.messageQueue
  }),
  websocketStatus: (state) => {
    const status = state.websocket
      ? state.websocket.getStatus()
      : {
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        reconnectAttempts: 0,
        messageQueueLength: 0,
        url: ''
      }
    return {
      ...status,
      config: state.config,
      lastError: state.lastError,
      unreadCount: state.unreadCount,
      loggedInUserId: state.loggedInUserId
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
